/**
 * FlahaFast Unified API Configuration
 *
 * Single API structure (no more v1/v2 confusion)
 * All endpoints use /api/* with unified controllers
 *
 * @version 2.0.0
 * @date January 6, 2025
 */

// Determine the unified API base URL based on environment
const getApiBaseUrl = (): string => {
  // In production, use relative URLs (same domain as the frontend)
  if (import.meta.env.MODE === 'production') {
    return '/api';
  }

  // In development, use localhost with the correct port
  return 'http://localhost:3001/api';
};

// Export the unified API base URL
export const API_BASE_URL = getApiBaseUrl();

// Unified API endpoint builders (combines best of v1 and v2)
export const apiEndpoints = {
  // System & Health
  health: `${API_BASE_URL}/health`,

  // Authentication endpoints
  auth: {
    login: `${API_BASE_URL}/auth/login`,
    register: `${API_BASE_URL}/auth/register`,
    logout: `${API_BASE_URL}/auth/logout`,
    me: `${API_BASE_URL}/auth/me`,
    profile: `${API_BASE_URL}/user/profile`,
    refreshToken: `${API_BASE_URL}/auth/refresh-token`,
    refresh: `${API_BASE_URL}/auth/refresh`,
    changePassword: `${API_BASE_URL}/auth/change-password`,
  },

  // Elements endpoints
  elements: {
    list: `${API_BASE_URL}/elements`,
    search: `${API_BASE_URL}/elements/search`,
    bySymbol: (symbol: string) => `${API_BASE_URL}/elements/symbol/${symbol}`,
    byId: (id: string) => `${API_BASE_URL}/elements/${id}`,
    create: `${API_BASE_URL}/elements`,
    update: (id: string) => `${API_BASE_URL}/elements/${id}`,
    delete: (id: string) => `${API_BASE_URL}/elements/${id}`,
  },

  // Salts endpoints
  salts: {
    list: `${API_BASE_URL}/salts`,
    search: `${API_BASE_URL}/salts/search`,
    custom: `${API_BASE_URL}/salts/custom`,
    byId: (id: string) => `${API_BASE_URL}/salts/${id}`,
    create: `${API_BASE_URL}/salts`,
    update: (id: string) => `${API_BASE_URL}/salts/${id}`,
    delete: (id: string) => `${API_BASE_URL}/salts/${id}`,
  },

  // Water Profiles endpoints
  waterProfiles: {
    list: `${API_BASE_URL}/water-profiles`,
    search: `${API_BASE_URL}/water-profiles/search`,
    byId: (id: string) => `${API_BASE_URL}/water-profiles/${id}`,
    create: `${API_BASE_URL}/water-profiles`,
    update: (id: string) => `${API_BASE_URL}/water-profiles/${id}`,
    delete: (id: string) => `${API_BASE_URL}/water-profiles/${id}`,
  },

  // Water Quality endpoints (Enhanced)
  waterQuality: {
    list: `${API_BASE_URL}/water-quality`,
    analyze: `${API_BASE_URL}/water-quality/analyze`,
    optimalParameters: (cropType: string) => `${API_BASE_URL}/water-quality/optimal-parameters/${cropType}`,
    byId: (id: string) => `${API_BASE_URL}/water-quality/${id}`,
    create: `${API_BASE_URL}/water-quality`,
    update: (id: string) => `${API_BASE_URL}/water-quality/${id}`,
    delete: (id: string) => `${API_BASE_URL}/water-quality/${id}`,
  },

  // Formulations endpoints (Enhanced)
  formulations: {
    list: `${API_BASE_URL}/formulations`,
    search: `${API_BASE_URL}/formulations/search`,
    byId: (id: string) => `${API_BASE_URL}/formulations/${id}`,
    create: `${API_BASE_URL}/formulations`,
    update: (id: string) => `${API_BASE_URL}/formulations/${id}`,
    delete: (id: string) => `${API_BASE_URL}/formulations/${id}`,
  },

  // Calculations endpoints (Comprehensive)
  calculations: {
    stockSolution: `${API_BASE_URL}/calculations/stock-solution`,
    stoichiometry: `${API_BASE_URL}/calculations/stoichiometry`,
    scaling: `${API_BASE_URL}/calculations/scaling`,
    precision: `${API_BASE_URL}/calculations/precision`,
    batch: `${API_BASE_URL}/calculations/batch`,
    stock: `${API_BASE_URL}/calculations/stock`,
    save: `${API_BASE_URL}/calculations/save`,
    optimizeStockSolutions: `${API_BASE_URL}/calculations/optimize-stock-solutions`,
    verifyStockSolutions: `${API_BASE_URL}/calculations/verify-stock-solutions`,
    splitStockSolution: `${API_BASE_URL}/calculations/split-stock-solution`,
  },

  // pH Adjustment endpoints
  ph: {
    products: `${API_BASE_URL}/ph/products`,
    calculate: `${API_BASE_URL}/ph/calculate`,
    alkalinity: `${API_BASE_URL}/ph/alkalinity`,
    predict: `${API_BASE_URL}/ph/predict`,
    recommendations: `${API_BASE_URL}/ph/recommendations`,
  },

  // Crop Recommendations endpoints
  crops: {
    types: `${API_BASE_URL}/crops/types`,
    recommendations: `${API_BASE_URL}/crops/recommendations`,
    compare: `${API_BASE_URL}/crops/compare`,
    waterQualityAdjustments: `${API_BASE_URL}/crops/water-quality-adjustments`,
  },
};

// Export utility functions
export const buildUrl = (endpoint: string, params?: Record<string, string | number>): string => {
  if (!params) return endpoint;
  
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    searchParams.append(key, String(value));
  });
  
  return `${endpoint}?${searchParams.toString()}`;
};

// Export environment information
export const isProduction = import.meta.env.MODE === 'production';
export const isDevelopment = import.meta.env.MODE === 'development';

// Default axios configuration
export const defaultAxiosConfig = {
  timeout: 10000, // 10 seconds
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for authentication
};

// Export for debugging (reduced logging)
if (isDevelopment && window.location.search.includes('debug=api')) {
  console.log('FlahaFast Unified API Configuration:', {
    NODE_ENV: import.meta.env.MODE,
    API_BASE_URL,
    version: '2.0.0',
    isProduction,
    isDevelopment,
  });
}
